import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import FinancingType from '~/constants/FinancingType';
import type { FinanceInfo, TradeInfo } from '~/contracts';

type ContextType = {
  financeInfo: FinanceInfo;
  setFinanceInfo: (financeInfo: FinanceInfo | undefined) => void;
  setTradeInfo: React.Dispatch<React.SetStateAction<TradeInfo | undefined>>;
  tradeInfo?: TradeInfo;
};

const defaultContext: ContextType = {
  financeInfo: {
    baseMinDownPayment: undefined,
    coApplicantToggleOn: undefined,
    coApplicantEligibility: undefined,
    financingType: FinancingType.EstimatedFinancing,
    hasCoApplicant: undefined,
    maxMonthlyPayment: 2000,
    maxTerm: undefined,
    minDownPayment: 0
  },
  setFinanceInfo() {},
  setTradeInfo() {},
  tradeInfo: undefined
};

const FinanceAndTradeContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  financeInfo?: FinanceInfo;
  tradeInfo?: TradeInfo;
}>;

export function FinanceAndTradeContextProvider(props: OwnProps) {
  const [financeInfo, setFinanceInfo] = useState(
    props.financeInfo ?? defaultContext.financeInfo
  );
  const [tradeInfo, setTradeInfo] = useState(
    props.tradeInfo ?? defaultContext.tradeInfo
  );

  const setFinanceInfoWithDefault = useCallback(
    (nextFinanceInfo: FinanceInfo | undefined) => {
      setFinanceInfo(nextFinanceInfo ?? defaultContext.financeInfo);
    },
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      financeInfo,
      setFinanceInfo: setFinanceInfoWithDefault,
      setTradeInfo,
      tradeInfo
    }),
    [financeInfo, setFinanceInfoWithDefault, tradeInfo]
  );

  return (
    <FinanceAndTradeContext.Provider value={value}>
      {props.children}
    </FinanceAndTradeContext.Provider>
  );
}

export function useFinanceInfo(): FinanceInfo {
  return (
    useContext(FinanceAndTradeContext).financeInfo ?? defaultContext.financeInfo
  );
}

export function useFinanceType(): FinancingType | undefined {
  return useFinanceInfo().financingType;
}

export function useTradeInfo(): TradeInfo | undefined {
  return useContext(FinanceAndTradeContext).tradeInfo;
}

export default FinanceAndTradeContext;
